import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { RangePicker } from '@/entities/range-picker';
import { Box } from '@/shared/ui/box';
import { Input } from '@/shared/ui/input';
import { Text } from '@/shared/ui/text';

import { subtractFromCurrentTime, timeRanges } from '../model/constants/time-ranges.constant';
import { generateText } from '../model/helpers/generate-text.helper';
import * as Styled from './grafana-range-picker.styles';
import { GrafanaRangePickerProps, OnChangeQuickRanges } from './grafana-range-picker.types';

const GrafanaRangePickerComponent = ({
	defaultLabel = 'Select time range',
	onChangeEndDate,
	startDate,
	endDate,
	onChangeStartDate,
	margin,
}: GrafanaRangePickerProps) => {
	const [label, setLabel] = useState('');
	const [filter, setFilter] = useState('');
	const items = useMemo(
		() => timeRanges.filter(({ label }) => label.toLowerCase().includes(filter.toLowerCase())),
		[filter],
	);

	const onChangeQuickRanges = useCallback(
		({ endDate, startDate, label, close }: OnChangeQuickRanges) =>
			() => {
				onChangeStartDate(startDate);
				onChangeEndDate(endDate);
				close();
				setLabel(label);
			},
		[onChangeStartDate, onChangeEndDate],
	);

	const onChangeFilterValue = useCallback((value: string) => {
		setFilter(value);
	}, []);

	const onOverrideOnChange = useCallback(
		(onChangeFunc: (date?: Date) => void) => (date?: Date) => {
			setLabel('');
			onChangeFunc(date);
		},
		[],
	);

	useEffect(() => {
		setLabel('Last 5 minutes');
		onChangeStartDate(subtractFromCurrentTime(5));
		onChangeEndDate(new Date());
	}, [onChangeStartDate, onChangeEndDate]);

	return (
		<Styled.Wrapper>
			{({ open, close }) => (
				<>
					<Styled.LabelWrapper open={open} margin={margin}>
						{generateText({ label, defaultLabel, endDate, startDate })}
					</Styled.LabelWrapper>
					<Styled.Content>
						<Box direction="column" flex={1} gap={16} minHeight="195px">
							<Text label="Absolute time range" />
							<RangePicker
								marginLeft={0}
								marginBottom={0}
								direction="column"
								startDate={startDate}
								endDate={endDate}
								onChangeStartDate={onOverrideOnChange(onChangeStartDate)}
								onChangeEndDate={onOverrideOnChange(onChangeEndDate)}
								showTimeInput
								startDateLabel="From"
								endDateLabel="To"
							/>
						</Box>
						<Box direction="column" gap={8}>
							<Input placeholder="Search quick ranges" value={filter} onChange={onChangeFilterValue} />
							<Box overflow="auto" direction="column" maxHeight="150px">
								{items.map(({ startDate, endDate, label }) => (
									<Styled.TimeText
										key={label}
										label={label}
										onClick={onChangeQuickRanges({ startDate, endDate, close, label })}
									/>
								))}
							</Box>
						</Box>
					</Styled.Content>
				</>
			)}
		</Styled.Wrapper>
	);
};

export default memo(GrafanaRangePickerComponent);
